/**
 * Abstract Singleton Storage Interfaces using a standardized
 *     storage object type and expiration policy.
 * @abstract
 * @name abstract-storage
 */
(function (root, factory) {
    if (typeof define === 'function' && define.amd) {
        // AMD. Register as an anonymous module.
        define(factory);
    } else if (typeof module === 'object' && module.exports) {
        // CommonJS
        module.exports = factory();
    }
})(this, function () {
    var UNDEF;
    var OBJECT = 'object';

    var errorCodes = {
        NOT_FOUND: 'object not found',
        INVALID_KEY: 'invalid key or namespace',
        NOT_STORABLE: 'object must be a valid instance of StorageEntry'
    };

    function now () {
        return Date.now();
    }
    /**
     * Storage object constructor.  Used as a standard container
     * for stored data in abstract-storage implementations.
     * @constructor
     * @param {string} key     internal reference to storage key for the object instance.
     * @param {mixed}  value   value to stored.
     * @param {number} expires make the storage object expired after expire seconds.
     *                         (-1 denotes a permanent object - no expire)
     */
    function StorageEntry (namespace, key, value, expires) {
        this.namespace = namespace;
        this.created = now();
        this.expires = (expires > 0 ? expires : expires === 0);
        this.key = key;
        this.value = value;
        this.expired = this.expires !== true ? 0 : 1;
    }

    /**
     * Procedure for retrieving a storage object.
     * If expired as dicated by the created and expires properties,
     * the object will be marked accordingly via the expired property.
     * (If none exists, return an empty expired object)
     *
     * @param  {object} so  A storage object to be evaluated for retrieval.
     * @return {object}     Object so or an 'empty' storage object.
     */
    function get (so) {
        if (so && so.expired !== 1 && ((so.expires === false) || ((so.created + (so.expires * 1000)) > now()))) {
            return so;
        }
        return expire(so) || getEmpty();
    }

    /**
     * Return an 'empty', data-less storage object.
     * @param  [{string}] code  Error code to be supplied to the object.
     * @return {object}         Object so or an 'empty' storage object.
     */
    function getEmpty (code) {
        var empty = storable(UNDEF, UNDEF, null, true);
        empty.created = 0;
        // empty.expired=1;
        empty.error = code || errorCodes.NOT_FOUND;
        return empty;
    }

    /**
     * Mark a storage object as expired.
     * @param  {object} so  Storage object to expire.
     */
    function expire (so) {
        if (so) {
            so.expired = 1;
        }
        return so;
    }

    /**
     * Create a storage entry object for the correspondig key/value pair.
     * @param  {object} so  Storage object to expire.
     * @return {object}     Object so or an 'empty' storage object.
     */
    function storable (namespace, key, value, expires) {
        // Use internal get to cover immediate exipires.
        return get(new StorageEntry(namespace, key, value, expires));
    }

    /**
     * Check if a key is valid for storage.
     * @param  {mixed}  key  Potential storage key.
     * @return {boolean}     Object so or an 'empty' storage object.
     */
    function validKeys (namespace, key) {
        return (key !== UNDEF && typeof key !== OBJECT) && (namespace !== UNDEF && typeof namespace !== OBJECT);
    }

    return {
        /**
         * Expose the StorageEntry object for testing.
         * @type {constructor}
         */
        StorageEntry: StorageEntry,
        /**
         * @see expire
         * @type {function}
         */
        expire: expire,
        /**
         * @see get
         * @type {function}
         */
        get: get,
        /**
         * @see storable
         * @type {function}
         */
        storable: storable,
        /**
         * @see getEmpty
         * @type {function}
         */
        getEmpty: getEmpty,

        validKeys: validKeys,

        errorCodes: errorCodes
    };
});
